/* Author-facing contracts card + withholding preview.
 * Renders every WIPO contract on the author's books plus one-tap sign
 * + PDF download. Withholding-preview strip surfaces the effective
 * rate implied by the caller's current tax profile so the value of
 * filing a W-8BEN or PAN is visible on entry.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, PenTool, Loader2, Download, Landmark, Plus } from 'lucide-react';
import SignContractDialog from './SignContractDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS_TONES = {
  draft:             'bg-slate-500/20 text-slate-700 dark:text-slate-300',
  partially_signed:  'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  signed:            'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
};

export function WithholdingPreviewStrip() {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    http.get('/authors/me/withholding-preview')
      .then((r) => setPreview(r.data))
      .catch(() => {});
  }, []);
  if (!preview) return null;
  const zero = preview.rate_pct === 0;
  return (
    <div
      data-testid="withholding-preview-strip"
      className={`rounded-md border p-3 ${zero
        ? 'border-emerald-500/40 bg-emerald-500/5'
        : 'border-amber-500/40 bg-amber-500/5'}`}
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        <Landmark className="h-4 w-4" />
        Payout withholding preview · {preview.country}
      </div>
      <div className="mt-1 text-[12px]">
        Effective rate: <strong>{preview.rate_pct}%</strong>
        {' · '}
        On a $100 payout you would keep <strong>${preview.sample_net_usd.toFixed(2)}</strong>
        {' '}(${preview.sample_withholding_usd.toFixed(2)} withheld).
      </div>
      {preview.reasons?.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-[11px] text-muted-foreground">
          {preview.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      <div className="mt-1 text-[10.5px] italic text-muted-foreground">{preview.note}</div>
    </div>
  );
}

export default function MyContractsCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signingFor, setSigningFor] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [books, setBooks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({ book_id: '', template_id: '', fields: {} });

  const [statusFilter, setStatusFilter] = useState('all');
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const q = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [r, t, s] = await Promise.all([
        http.get(`/authors/me/contracts${q}`),
        http.get('/wipo/templates'),
        http.get('/authors/me/ohas/summary'),
      ]);
      setRows(r.data.contracts || []);
      setTemplates(t.data.templates || []);
      setBooks(s.data.books || []);
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!newDraft.book_id || !newDraft.template_id) return toast.error('Pick a book + template.');
    try {
      await http.post('/authors/me/contracts', newDraft);
      toast.success('Contract draft created.');
      setCreating(false);
      setNewDraft({ book_id: '', template_id: '', fields: {} });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Create failed.'); }
  };

  const tpl = templates.find((t) => t.id === newDraft.template_id);

  const sign = (c) => setSigningFor(c);

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="my-contracts-card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <FileText className="h-4 w-4 text-primary" /> My Contracts
        </div>
        <div className="flex items-center gap-2">
          {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="my-contracts-filter"
            className="h-7 rounded-sm border border-border bg-background px-1.5 text-[10.5px]"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="partially_signed">Partially signed</option>
            <option value="signed">Signed</option>
          </select>
          <a
            href={`${API}/authors/me/contracts/signed-vault.zip`}
            target="_blank" rel="noreferrer"
            data-testid="my-contracts-vault-download"
            className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10.5px] font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
          >
            <Download className="h-3 w-3" /> Vault ZIP
          </a>
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            data-testid="my-contracts-new"
            className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10.5px] font-semibold text-primary hover:bg-primary/20"
          >
            <Plus className="h-3 w-3" /> New contract
          </button>
        </div>
      </div>
      <p className="mb-3 text-[11.5px] text-muted-foreground">
        WIPO Model publishing contracts on your books. Sign in-app —
        every signature carries a SHA-256 tamper seal.
      </p>
      <WithholdingPreviewStrip />
      {creating && (
        <div className="mt-3 rounded-md border border-dashed border-border bg-panel-header/40 p-3" data-testid="my-contracts-new-form">
          <div className="grid gap-2 md:grid-cols-2">
            <label>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Book</div>
              <select className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
                      value={newDraft.book_id}
                      data-testid="my-contracts-book"
                      onChange={(e) => setNewDraft({ ...newDraft, book_id: e.target.value })}>
                <option value="">—</option>
                {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
              </select>
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Template</div>
              <select className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
                      value={newDraft.template_id}
                      data-testid="my-contracts-template"
                      onChange={(e) => setNewDraft({ ...newDraft, template_id: e.target.value, fields: {} })}>
                <option value="">—</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </label>
          </div>
          {tpl && (
            <div className="mt-2 grid gap-1.5 md:grid-cols-2">
              {tpl.fields.slice(0, 8).map((f) => (
                <label key={f}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{f}</div>
                  <input
                    className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-1.5 text-[11.5px]"
                    value={newDraft.fields[f] || ''}
                    onChange={(e) => setNewDraft({ ...newDraft, fields: { ...newDraft.fields, [f]: e.target.value } })}
                  />
                </label>
              ))}
            </div>
          )}
          <div className="mt-2">
            <button type="button" onClick={submitNew}
                    data-testid="my-contracts-create"
                    className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90">
              Create draft
            </button>
          </div>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {rows.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-3 text-[11.5px] text-muted-foreground">
            No contracts on your books yet. Click "New contract" above or ask an admin to draft one.
          </div>
        )}
        {rows.map((c) => {
          const signedRoles = new Set((c.signatures || []).map((s) => s.signer_role));
          const iSigned = signedRoles.has('author');
          return (
            <div key={c.id} className="rounded-md border border-border bg-panel-header/40 p-2" data-testid={`my-contract-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-semibold">{c.template_title}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {c.book_title || c.book_id} · {c.created_at?.slice(0, 10)}
                  </div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONES[c.status] || 'bg-slate-500/20 text-slate-700'}`}>
                  {c.status.replace('_', ' ')}
                </span>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button" disabled={iSigned}
                  onClick={() => sign(c)}
                  data-testid={`my-contract-sign-${c.id}`}
                  className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10.5px] ${
                    iSigned ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700' : 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  }`}
                >
                  <PenTool className="h-3 w-3" /> {iSigned ? 'You signed ✓' : 'Sign as author'}
                </button>
                <a
                  href={`${API}/authors/me/contracts/id/${c.id}/pdf`}
                  target="_blank" rel="noreferrer"
                  data-testid={`my-contract-pdf-${c.id}`}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary"
                >
                  <Download className="h-3 w-3" /> PDF
                </a>
                <button
                  type="button"
                  data-testid={`my-contract-share-${c.id}`}
                  onClick={async () => {
                    const choice = window.prompt('Share link expiry — enter 24h, 7d, or 30d:', '7d');
                    const hours = choice === '24h' ? 24 : choice === '30d' ? 720 : 168;
                    try {
                      const r = await http.post(`/authors/me/contracts/id/${c.id}/share-link`, { expires_hours: hours });
                      const base = (process.env.REACT_APP_BACKEND_URL || '') + r.data.pdf_url;
                      await navigator.clipboard.writeText(base);
                      toast.success(`Share link (${choice || '7d'}) copied.`);
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Share failed.'); }
                  }}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary"
                >
                  <FileText className="h-3 w-3" /> Share link
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <SignContractDialog
        contract={signingFor}
        onClose={() => setSigningFor(null)}
        onSigned={load}
      />
    </div>
  );
}
